import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getFeatureParam,
  setFeatureParam,
  getAllFeatureParams,
  isOnboardingFlowCompleted,
  markOnboardingFlowCompleted,
  resetOnboardingFlowCompletion,
  FeatureParam
} from '@/services/feature-params/feature-params-service';

// Query keys
export const featureParamKeys = {
  all: ['featureParams'] as const,
  lists: () => [...featureParamKeys.all, 'list'] as const,
  list: (filters: any = {}) => [...featureParamKeys.lists(), filters] as const,
  details: () => [...featureParamKeys.all, 'detail'] as const,
  detail: (key: string) => [...featureParamKeys.details(), key] as const,
};

// Stale and garbage collection times
const STALE_TIME = 1000 * 60 * 5; // 5 minutes
const GC_TIME = 1000 * 60 * 10; // 10 minutes

/**
 * Hook to get all feature parameters
 */
export function useFeatureParams() {
  return useQuery({
    queryKey: featureParamKeys.lists(),
    queryFn: getAllFeatureParams,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to get a specific feature parameter by key
 * @param key The feature parameter key
 */
export function useFeatureParam(key: string) {
  return useQuery({
    queryKey: featureParamKeys.detail(key),
    queryFn: () => getFeatureParam(key),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!key,
  });
}

/**
 * Hook to check if the onboarding flow is completed
 */
export function useIsOnboardingFlowCompleted() {
  return useQuery({
    queryKey: featureParamKeys.detail('onboarding-flow-completed'),
    queryFn: isOnboardingFlowCompleted,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to update a feature parameter
 */
export function useUpdateFeatureParam() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: boolean }) => 
      setFeatureParam(key, value),
    onSuccess: (_, variables) => {
      // Invalidate the specific feature parameter
      queryClient.invalidateQueries({ 
        queryKey: featureParamKeys.detail(variables.key) 
      });
      
      // Also invalidate the list of all feature parameters
      queryClient.invalidateQueries({ 
        queryKey: featureParamKeys.lists() 
      });
    },
  });
}

/**
 * Hook to mark the onboarding flow as completed
 */
export function useMarkOnboardingFlowCompleted() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: markOnboardingFlowCompleted,
    onSuccess: () => {
      // Invalidate the onboarding flow completion status
      queryClient.invalidateQueries({ 
        queryKey: featureParamKeys.detail('onboarding-flow-completed') 
      });
      
      // Also invalidate the list of all feature parameters
      queryClient.invalidateQueries({ 
        queryKey: featureParamKeys.lists() 
      });
      
      // Optimistically update the cache
      queryClient.setQueryData(
        featureParamKeys.detail('onboarding-flow-completed'),
        true
      );
    },
  });
}

/**
 * Hook to reset the onboarding flow completion status
 */
export function useResetOnboardingFlowCompletion() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: resetOnboardingFlowCompletion,
    onSuccess: () => {
      // Invalidate the onboarding flow completion status
      queryClient.invalidateQueries({ 
        queryKey: featureParamKeys.detail('onboarding-flow-completed') 
      });
      
      // Also invalidate the list of all feature parameters
      queryClient.invalidateQueries({ 
        queryKey: featureParamKeys.lists() 
      });
      
      // Optimistically update the cache
      queryClient.setQueryData(
        featureParamKeys.detail('onboarding-flow-completed'),
        false
      );
    },
  });
}
